import axios from 'axios'
import ReactDOM from 'react-dom'
import React from 'react'
import SweetAlertService from '../lib/utils/sweet-alert-service'
import {baseUrl} from './base-url'
import {checkUrlAlertException} from './no-err-alert-urls'
import {autoRefreshTokenIfNeeded} from '../lib/utils/auth.utils'
import {getCookie, setCookie} from 'react-use-cookie'

// 设置基础的url
axios.create({
  baseURL: baseUrl,
  timeout: 5000,
})

let requestCount = 0

// 防止401错误重复跳转的标志
let isRedirectingToLogin = false

// 暴露全局重置函数供其他模块使用
if (typeof window !== 'undefined') {
  // 简单的重置函数，避免复杂的事件监听
  window.reset401RedirectFlag = function() {
    isRedirectingToLogin = false
    console.log('🔄 401 redirect flag reset')
  }

  // 页面加载时重置一次
  window.addEventListener('load', function() {
    isRedirectingToLogin = false
    console.log('🔄 Page loaded, 401 redirect flag reset')
  })
}

const loaderStyles = `
  @keyframes wave {
    0%, 100% {
      transform: translateY(0) scale(1);
    }
    50% {
      transform: translateY(-15px) scale(1.1);
    }
  }

  @keyframes leafFloat {
    0%, 100% {
      transform: rotate(-30deg) translateY(0);
    }
    50% {
      transform: rotate(-30deg) translateY(-10px);
    }
  }

  @keyframes blink {
    0%, 20%, 100% {
      opacity: 1;
    }
    50% {
      opacity: 0.3;
    }
  }

  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }

  .jad-loader-overlay {
    position: fixed;
    top: 0;
    bottom: 0;
    left: 0;
    right: 0;
    background: rgba(0, 0, 0, 0.4);
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    z-index: 9999;
  }

  .jad-loader-container {
    text-align: center;
  }

  .jad-logo-text {
    font-size: 60px;
    font-weight: bold;
    color: #1a3d2e;
    letter-spacing: -2px;
    position: relative;
    display: inline-block;
  }

  .jad-logo-text .letter {
    display: inline-block;
    animation: wave 1.5s ease-in-out infinite;
    color: white;
  }

  .jad-logo-text .letter:nth-child(1) { animation-delay: 0s; }
  .jad-logo-text .letter:nth-child(2) { animation-delay: 0.1s; }
  .jad-logo-text .letter:nth-child(3) { animation-delay: 0.2s; }
  .jad-logo-text .letter:nth-child(4) { animation-delay: 0.3s; }
  .jad-logo-text .letter:nth-child(5) { animation-delay: 0.4s; }
  .jad-logo-text .letter:nth-child(6) { animation-delay: 0.5s; }

  .jad-leaf {
    display: inline-block;
    width: 30px;
    height: 45px;
    background: #7fbfa0;
    border-radius: 0 50% 50% 0;
    position: relative;
    margin-left: 8px;
    transform: rotate(-30deg);
    animation: leafFloat 2s ease-in-out infinite;
  }

  .jad-spinner {
    margin: 30px auto 15px;
    width: 50px;
    height: 50px;
    border: 4px solid rgba(255, 255, 255, 0.3);
    border-top: 4px solid #7fbfa0;
    border-radius: 50%;
    animation: spin 1s linear infinite;
  }

  .jad-loading-dots {
    margin-top: 10px;
    font-size: 18px;
    color: white;
  }

  .jad-loading-dots span {
    animation: blink 1.4s infinite;
    display: inline-block;
  }

  .jad-loading-dots span:nth-child(2) { animation-delay: 0.2s; }
  .jad-loading-dots span:nth-child(3) { animation-delay: 0.4s; }
`

// Inject styles
const injectStyles = () => {
  if (!document.getElementById('jad-loader-styles')) {
    const styleElement = document.createElement('style')
    styleElement.id = 'jad-loader-styles'
    styleElement.textContent = loaderStyles
    document.head.appendChild(styleElement)
  }
}

const JadCupLoader = () => (
  <div className="jad-loader-overlay">
    <div className="jad-loader-container">
      <div className="jad-logo-text">
        <span className="letter">J</span>
        <span className="letter">a</span>
        <span className="letter">d</span>
        <span className="jad-leaf"></span>
      </div>
      <div className="jad-logo-text" style={{marginTop: '-15px'}}>
        <span className="letter">c</span>
        <span className="letter">u</span>
        <span className="letter">p</span>
      </div>
      <div className="jad-spinner"></div>
      <div className="jad-loading-dots">
        Loading<span>.</span><span>.</span><span>.</span>
      </div>
    </div>
  </div>
)

const showLoading = () => {
  if (requestCount === 0) {
    injectStyles()
    let dom = document.createElement('div')
    dom.setAttribute('id', 'loading')
    document.body.append(dom)
    ReactDOM.render(<JadCupLoader />, dom)
  }
  requestCount++
}

const hideLoading = () => {
  requestCount--
  if (requestCount === 0) {
    const loadingElement = document.getElementById('loading')
    if (loadingElement) {
      document.body.removeChild(loadingElement)
    }
  }
}

axios.interceptors.request.use(async function (config) {
  // 检查是否已经在登录页面，避免循环
  const isLoginPage = window.location.pathname === '/' ||
                      window.location.href.includes('/login') ||
                      config.url?.includes('Login')

  // 只在非登录页面且非登录相关请求时刷新token
  if (!isLoginPage) {
    await autoRefreshTokenIfNeeded()
  }

  // 添加Authorization header，携带token
  const token = getCookie('token')

  // 调试日志：检查token状态
  if (!isLoginPage && !config.url?.includes('RefreshToken')) {
    console.log(`🔑 Token status for ${config.url}:`, {
      hasToken: !!token,
      tokenLength: token?.length || 0,
      tokenPreview: token ? `${token.substring(0, 20)}...` : 'NO_TOKEN'
    })
  }

  if (token && token.length > 0) {
    config.headers.Authorization = `Bearer ${token}`
    if (!isLoginPage && !config.url?.includes('RefreshToken')) {
      console.log(`✅ Authorization header added to: ${config.url}`)
    }
  } else if (!isLoginPage && !config.url?.includes('RefreshToken')) {
    console.warn(`⚠️ No token available for: ${config.url}`)
  }

  if (config.headers.isLoading !== false) {
    showLoading()
  }
  return config
}, function (error) {
  return Promise.reject(error)
})

axios.interceptors.response.use(function (response) {
  if (response.config.headers.isLoading !== false) {
    setTimeout(hideLoading, 1000)
    if (response.config.method !== 'get' && response.config.url !== 'Employee/VerifyPassword' 
      && response.config.url !== 'Employee/RefreshToken'  && checkUrlAlertException(response.config.url)) {
      SweetAlertService.successMessage('')
    }
  }
  return response
}, function (error) {
  if (error.response?.config.headers.isLoading !== false) {
    setTimeout(hideLoading, 1000)
  }
  console.log(error, '响应返回的错误信息')
  console.log(error.response, '响应返回的错误信息2')

  // 处理401未授权错误 - token过期或无效
  if (error.response?.status === 401) {
    // 检查是否在登录页面，避免循环
    const isLoginPage = window.location.pathname === '/' ||
                        window.location.pathname === '/login' ||
                        window.location.href.includes('/login')

    if (isLoginPage) {
      console.log('🚫 401 on login page, skipping redirect to avoid loop')
      return Promise.reject(error.response)
    }

    // 防止重复跳转
    if (isRedirectingToLogin) {
      console.log('⚠️ Already redirecting to login, skipping duplicate redirect')
      return Promise.reject(error.response)
    }

    console.error('🚫 401 Unauthorized Error:', {
      url: error.response.config.url,
      method: error.response.config.method,
      currentPath: window.location.pathname,
      hadAuthHeader: !!error.response.config.headers.Authorization,
      authHeaderPreview: error.response.config.headers.Authorization ?
        `${error.response.config.headers.Authorization.substring(0, 30)}...` : 'NO_AUTH_HEADER'
    })

    // 设置跳转标志
    isRedirectingToLogin = true

    // Clear the stale session BEFORE redirecting. Otherwise the token cookie
    // keeps `authenticated()` true, ProtectedRoute bounces '/' back to '/home',
    // the next request 401s again and the app enters an infinite redirect loop
    // (this really happens when the refresh also fails, e.g. changed password).
    try {
      localStorage.removeItem('pages')
      localStorage.removeItem('user_credentials')
      localStorage.removeItem('token_expires_at')
      setCookie('token', '')
      setCookie('email', '')
      setCookie('userName', '')
      setCookie('name', '')
      // Customer sessions have no token of their own; without clearing this
      // cookie the '/' route re-renders the customer portal, its APIs 401
      // again and the app loops between the customer page and /?expired=1.
      setCookie('customerUserId', '')
    } catch (e) {
      console.error('Failed to clear session on 401:', e)
    }

    // 直接跳转，不使用alert和setTimeout避免死循环
    console.log('🔄 Redirecting to login page due to 401 error')
    window.location.href = '/?expired=1'
    return Promise.reject(error.response)
  }

  if (error.response?.data && checkUrlAlertException(error.response?.config?.url)) {
    SweetAlertService.errorMessage(
      error.response?.data?.message?.message ||
      error.response?.data?.title ||
      'Failed! Please contact admin.',
      error.response?.data?.innerMessage || getErrorsDetails(error.response?.data?.errors)
    )
  } else {
    SweetAlertService.errorMessage('Network Error')
  }
  return Promise.reject(error.response)
})

const getErrorsDetails = (data) => {
  if (!data) {
    return ''
  }
  let result = ''
  for (const [key, value] of Object.entries(data)) {
    result += `${key}: [${value}] `
  }
  return result
}

export default axios
